/**
 * Diğer › Sekreter (v0.1.28). Top: the last list the Sekreter sent to Telegram, numbered, with
 * where each item is done. Below: Sercan's own items with a small form to add one; an item he
 * adds reaches the next Telegram list by itself (the worker reads open rows every half hour).
 * "Yapıldı" closes a row, "Geri al" reopens it within two weeks. Automatic items (missing
 * values, approvals, drafts) close themselves when the underlying thing is done.
 */

import { type FormEvent, useEffect, useState } from 'react';
import {
  actorLabel,
  SEKRETER_REASON_LABELS,
  type SekreterItemInput,
  type SekreterManualItem,
  type SekreterView,
} from '../api/sekreter.js';
import { useStore } from '../app/context.js';
import { type SekreterState, useSekreter } from '../app/sekreter.js';
import { formatRelativeClock } from '../lib/format.js';
import { Icon } from './icons.js';

const TITLE_MIN = 3;
const TITLE_MAX = 200;
const DETAIL_MAX = 600;
const PLACE_MAX = 120;

function StatusLine({ state, reload }: { state: SekreterState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Sekreter yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

function LastDigest({ view, now }: { view: SekreterView; now: number }) {
  if (view.sentAt === null) {
    return (
      <p className="kb-page__empty">Henüz liste gönderilmedi; ilk liste en geç yarım saat içinde Telegram’a gider.</p>
    );
  }
  const when = formatRelativeClock(new Date(view.sentAt).getTime(), now);
  const reason = view.reason === null ? '' : `, ${SEKRETER_REASON_LABELS[view.reason]}`;
  return (
    <>
      <p className="kb-card__meta">
        Son gönderim {when}
        {reason}. Liste değişince Sekreter kendisi yazar; bir maddeyi yapınca listeden düşer.
      </p>
      {view.items.length === 0 ? (
        <p className="kb-page__empty">Şu an senden bekleyen bir şey yok.</p>
      ) : (
        <ol className="sk-digest" aria-label="Senden bekleyenler">
          {view.items.map((item) => (
            <li key={item.key}>
              <span>{item.text}</span>
              {item.place === '' ? null : <span className="kb-card__meta"> · {item.place}</span>}
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

function AddForm() {
  const app = useStore();
  const { store, state } = useSekreter();
  const [title, setTitle] = useState('');
  const [place, setPlace] = useState('');
  const [detail, setDetail] = useState('');
  const busy = state.busy.includes('add');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const clean = title.trim();
    if ([...clean].length < TITLE_MIN) {
      app.showToast(`Başlık en az ${TITLE_MIN} karakter olmalı`, { icon: 'info' });
      return;
    }
    const input: SekreterItemInput = {
      title: clean,
      ...(place.trim() === '' ? {} : { place: place.trim() }),
      ...(detail.trim() === '' ? {} : { detail: detail.trim() }),
    };
    const error = await store.add(input);
    if (error === null) {
      setTitle('');
      setPlace('');
      setDetail('');
      app.showToast('Eklendi; Sekreter bir sonraki listede yazar', { icon: 'check' });
    } else {
      app.showToast(`Eklenemedi: ${error}`, { icon: 'alert' });
    }
  };
  return (
    <form className="sk-form" aria-label="Madde ekle" onSubmit={(event) => void submit(event)}>
      <label className="kb-field">
        <span className="kb-field__label">Başlık</span>
        <input
          className="kb-field__text"
          value={title}
          maxLength={TITLE_MAX}
          placeholder="Ne yapılacak? (ör. B2 yedek kovasını aç)"
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <label className="kb-field">
        <span className="kb-field__label">Nerede</span>
        <input
          className="kb-field__text"
          value={place}
          maxLength={PLACE_MAX}
          placeholder="Ekran veya site (isteğe bağlı)"
          onChange={(event) => setPlace(event.target.value)}
        />
      </label>
      <label className="kb-field">
        <span className="kb-field__label">Ayrıntı</span>
        <textarea
          className="kb-field__text"
          value={detail}
          maxLength={DETAIL_MAX}
          rows={2}
          placeholder="Gerekirse bir iki cümle (isteğe bağlı)"
          onChange={(event) => setDetail(event.target.value)}
        />
      </label>
      <button type="submit" className="btn btn--primary" disabled={busy}>
        <Icon name="plus" size={16} />
        <span>Ekle</span>
      </button>
    </form>
  );
}

function OwnItem({ item, now, done }: { item: SekreterManualItem; now: number; done: boolean }) {
  const app = useStore();
  const { store, state } = useSekreter();
  const busy = state.busy.includes(item.itemId);
  const toggle = async () => {
    const error = done ? await store.reopen(item.itemId) : await store.done(item.itemId);
    if (error === null) app.showToast(done ? 'Yeniden açıldı' : 'Yapıldı olarak kapandı', { icon: 'check' });
    else app.showToast(`Kaydedilemedi: ${error}`, { icon: 'alert' });
  };
  const stamp =
    done && item.doneAt !== null
      ? `yapıldı ${formatRelativeClock(new Date(item.doneAt).getTime(), now)}`
      : `eklendi ${formatRelativeClock(new Date(item.createdAt).getTime(), now)}, ${actorLabel(item.createdBy)}`;
  return (
    <li className={`kb-card${done ? ' kb-card--retired' : ''}`} aria-label={item.title}>
      <header className="kb-card__head">
        <div className="kb-card__title">
          <strong>{item.title}</strong>
          <span className="kb-card__meta">
            {item.place === null ? '' : `${item.place} · `}
            {stamp}
          </span>
        </div>
      </header>
      {item.detail === null ? null : <p className="kb-card__pattern">{item.detail}</p>}
      <div className="kb-card__actions">
        <button type="button" className="btn btn--secondary" disabled={busy} onClick={() => void toggle()}>
          <Icon name={done ? 'refresh-cw' : 'check'} size={16} />
          <span>{done ? 'Geri al' : 'Yapıldı'}</span>
        </button>
      </div>
    </li>
  );
}

export function SekreterPage({ now }: { now: number }) {
  const { store, state } = useSekreter();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  if (state.view === null) return <StatusLine state={state} reload={() => void store.reload()} />;
  const view = state.view;
  const open = view.manual.filter((item) => item.doneAt === null);
  const done = view.manual.filter((item) => item.doneAt !== null);
  return (
    <section className="kb-page" aria-label="Sekreter">
      {/* The section page says what this is in one line; the longer help opens here (audit 25 Oct 2026, F18). */}
      <details className="kb-page__help">
        <summary className="kb-page__help-summary">
          <Icon name="chevron-down" size={16} />
          Nasıl çalışır
        </summary>
        <p className="kb-page__lead">
          Sekreter senden bekleyenleri sayar ve Telegram’a yazar: girilmemiş bağlantı değerleri, Etsy izni, bekleyen
          onaylar, bilgi bankası taslakları, tarama kararı ve buraya yazdığın maddeler. Bot komutu{' '}
          <code>/bekleyenler</code> aynı listeyi gösterir. Sekreter bir model değil, kod: hiçbir şeyi kendisi yapmaz.
        </p>
      </details>
      <h2 className="sk-heading">Son gönderilen liste</h2>
      <LastDigest view={view} now={now} />
      <h2 className="sk-heading">Kendi maddelerin</h2>
      <AddForm />
      {open.length === 0 ? (
        <p className="kb-page__empty">Açık maddeniz yok.</p>
      ) : (
        <ul className="kb-list" aria-label="Kendi maddelerin">
          {open.map((item) => (
            <OwnItem key={item.itemId} item={item} now={now} done={false} />
          ))}
        </ul>
      )}
      {done.length === 0 ? null : (
        <details className="sk-done">
          <summary>Yapılanlar ({done.length})</summary>
          <ul className="kb-list" aria-label="Yapılan maddeler">
            {done.map((item) => (
              <OwnItem key={item.itemId} item={item} now={now} done={true} />
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
